import { uniqBy } from 'lodash';
import type {
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import {
  type PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { fieldUtils } from '@ardoq/scope-data';

/** A field attribute set enriched with the current user's permission view. */
export type FieldWithPermissions = APIFieldAttributes & {
  isConfidential: boolean;
};

export const isConfidentialField = (
  permissionContext: PermissionContext,
  workspaceId: string,
  fieldName: string
): boolean =>
  !permissionsOperations.canSeeConfidentialFieldByName(
    permissionContext,
    fieldName,
    workspaceId
  );

export const enhanceAttributesWithPermissions = (
  permissionContext: PermissionContext,
  attributes: APIFieldAttributes,
  workspaceId?: ArdoqId | null
): FieldWithPermissions => ({
  ...attributes,
  isConfidential: workspaceId
    ? isConfidentialField(permissionContext, workspaceId, attributes.name)
    : false,
});

export type FieldInstance = {
  field: APIFieldAttributes;
  workspaceId: ArdoqId;
};

type GetFieldsForViewpointArgs = {
  scopeData: EnhancedScopeData;
  permissionContext: PermissionContext;
  /** Workspaces the viewpoint spans (typically the root workspaces of the start set). */
  workspaceIds: ArdoqId[];
  /** Which field types to include (e.g. NUMBER, LIST, DATE_TIME). */
  types: APIFieldType[];
  /**
   * Component type IDs actually present in the view. A field is included
   * if it's global or attached to at least one of these component types.
   * Pass an empty set to drop the relevance filter (all field instances pass).
   */
  relevantComponentTypeIds: Set<ArdoqId>;
};

type GetFieldInstancesForViewpointArgs = Pick<
  GetFieldsForViewpointArgs,
  'scopeData' | 'workspaceIds' | 'types'
>;

export const getFieldInstancesForViewpoint = ({
  scopeData,
  workspaceIds,
  types,
}: GetFieldInstancesForViewpointArgs): FieldInstance[] =>
  workspaceIds.flatMap(workspaceId => {
    if (!scopeData.workspacesById[workspaceId]) return [];
    return fieldUtils
      .getAllFieldsInWorkspace(scopeData, workspaceId)
      .filter(field => types.includes(field.type))
      .map(field => ({ field, workspaceId }));
  });

export const getRelevantFieldInstances = (
  fieldInstances: FieldInstance[],
  relevantComponentTypeIds: Set<ArdoqId>
): FieldInstance[] => {
  if (relevantComponentTypeIds.size === 0) return fieldInstances;
  return fieldInstances.filter(
    ({ field }) =>
      field.global ||
      field.componentType.some(t => relevantComponentTypeIds.has(t))
  );
};

type ToFieldsForViewpointArgs = Pick<
  GetFieldsForViewpointArgs,
  'permissionContext' | 'relevantComponentTypeIds'
> & {
  fieldInstances: FieldInstance[];
};

export const toFieldsForViewpoint = ({
  permissionContext,
  fieldInstances,
  relevantComponentTypeIds,
}: ToFieldsForViewpointArgs): FieldWithPermissions[] =>
  uniqBy(
    getRelevantFieldInstances(fieldInstances, relevantComponentTypeIds),
    ({ field }) => field.name
  ).map(({ field, workspaceId }) =>
    enhanceAttributesWithPermissions(permissionContext, field, workspaceId)
  );

/**
 * Resolve the field options to show in a viewpoint-mode picker.
 *
 *  - Sources field attributes from `scopeData` (no live model-interface reads).
 *  - Enriches each field with `isConfidential` based on the current user's permissions.
 *  - Drops fields whose component types aren't represented in the view (when the relevance set is non-empty).
 *  - Deduplicates by field name so the same field defined across multiple
 *    workspaces shows up once — selections in these pickers are name-only,
 *    so duplicates would visually highlight together and confuse users.
 *
 * See ARD-32072 for the duplicate-field symptom this addresses in CM2.
 */
export const getFieldsForViewpoint = ({
  scopeData,
  permissionContext,
  workspaceIds,
  types,
  relevantComponentTypeIds,
}: GetFieldsForViewpointArgs): FieldWithPermissions[] =>
  toFieldsForViewpoint({
    permissionContext,
    fieldInstances: getFieldInstancesForViewpoint({
      scopeData,
      workspaceIds,
      types,
    }),
    relevantComponentTypeIds,
  });
